import React, { useLayoutEffect } from 'react';
import { Box, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import floorImg from '../../assets/img/floor.jpg';
import { getGrainBumpTexture } from '../utils/grainTexture';

const Wall = ({
  wallWidth,
  wallHeight,
  wallDepth,
  wallDepthRight = wallDepth,
  roomShape = 0,
  skirtingType = 0,
  cabinetDepth = 0,
}) => {
  const ww = wallWidth / 100;
  const wh = wallHeight / 100;
  
  const wdLeft = wallDepth / 100;
  const wdRight = wallDepthRight / 100;

  const showLeft = roomShape === 1 || roomShape === 2;
  const showRight = roomShape === 1 || roomShape === 3;

  // Grindų/lubų gylis turi remtis TIK realiai matomomis sienomis — jei matoma tik viena
  // pusė, kitos (nerodomos) pusės sukonfigūruotas gylis neturi jokios įtakos, kitaip
  // grindys/lubos "iškištų" pagal nematomos sienos gylį. Kai matomos abi sienos, imamas
  // didesnis iš dviejų, kad grindys/lubos pasiektų tolimesnę sieną.
  // `cabinetDepth` (baldo gilumiausio taško Z, iš Builder3D.jsx) taip pat įtraukiamas —
  // baldo gylis nebėra ribojamas patalpos gyliu (žr. Layout.jsx onDimensionChange 'depth'
  // šaka), tad kai baldas iš tikrųjų giliau nei bet kuri siena, GRINDYS/LUBOS pratęsiamos
  // iki jo, o pačios ŠONINĖS/GALINĖ sienos IŠLIEKA savo (mažesnio) gylio — žr. ClickUp
  // "1.0 Patalpa. Dabar mažesnis patalpos gylis riboja max. baldo gylį... sienos lieka
  // mažesnio gylio, o grindys didėja kartu su baldu".
  const maxFloorDepth = Math.max(
    showLeft && showRight
      ? Math.max(wdLeft, wdRight)
      : showRight
        ? wdRight
        : wdLeft,
    Number(cabinetDepth) || 0
  );

  const wt = 0.1; // Sienos storis

  // Skirting type logika
  const isObject = typeof skirtingType === 'object' && skirtingType !== null;
  const sType = isObject ? skirtingType.type : skirtingType;
  
  const skirtingHeight = (isObject && skirtingType.height) ? skirtingType.height / 100 : 0.08; 
  const skirtingThickness = (isObject && skirtingType.depth) ? skirtingType.depth / 100 : 0.015; 
  
  const renderSkirting = sType === 1 || sType === 2; 

  const showBack = true;

  // "ww" yra GRYNAS/švarus grindų (baldų statymo) plotis — visada, nepriklausomai nuo
  // šoninių sienų. Kai šoninės sienos yra, jos pridedamos PRIE šio pločio IŠORĖJE
  // (analogiškai galinei sienai, kuri stovi už grindų z=0 krašto), o ne atimamos iš jo.
  const leftInset = showLeft ? wt : 0;
  const rightInset = showRight ? wt : 0;
  const floorWidth = ww;
  const floorX = ww / 2;

  const texture = useTexture(floorImg);
  // Kambario sienos/lubos anksčiau buvo visiškai lygi vienspalvė medžiaga — jokio
  // roughness/bump varijavimo, todėl net žemesnis apšvietimas neturėjo ką "pagriebti"
  // ir atrodė kaip plokščias fonas, ne tinkuota siena. Ta pati subtili procedūrinė
  // grūdėtumo tekstūra kaip ant baldo skydų (grainTexture.js), tik silpnesnė (sienos —
  // tinkuotos/dažytos, turi būti lygesnės už medžio plokštę).
  const wallBump = getGrainBumpTexture();
  const WALL_BUMP_SCALE = 0.001;

  // Nustatome tekstūros kartojimąsi pagal realius matmenis,
  // kad grindys neišsitemptų, kai siena labai ilga.
  useLayoutEffect(() => {
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(floorWidth / 2, maxFloorDepth / 2);
  }, [texture, floorWidth, maxFloorDepth]);

  return (
    <group>
      {/* Grindys — tarp šoninių sienų vidinių paviršių. raycast={()=>null}: šis
          sluoksnis yra grynai vizualinis (jokio onClick/onPointer visame Wall.jsx),
          bet driekiasi per VISĄ kambario plotį/gylį prie pat Y=0 — arti TOS PAČIOS
          Y zonos, kurioje stovi baldo Pagrindo/Cokolio paspaudžiami elementai, tad
          gali laimėti raycast konkurenciją prieš juos (žr. Lubos komentarą žemiau —
          patvirtinta vartotojo pranešta problema, kad lubos TIKRAI taip blokavo
          Stogelio paspaudimą; grindys turi tą pačią geometrinę prielaidą, taisoma
          kartu, kol neatsirado atskiro pranešimo).
      */}
      <Box
        position={[floorX, -0.025, maxFloorDepth / 2]}
        args={[floorWidth, 0.05, maxFloorDepth]}
        raycast={() => null}
      >
        <meshStandardMaterial map={texture} />
      </Box>

      {/* Lubos — tarp šoninių sienų vidinių paviršių. raycast={()=>null}: patikrinta
          tiesiogiai per rankinį spindulio (ray-box) skaičiavimą — šis sluoksnis
          (visas kambario plotis, Y=wh+0.025, per visą kambario gylį) FIZIŠKAI
          UŽSTOJA paspaudimus, skirtus Stogelio dangos plokštei (žr. UnifiedTopPanel.jsx
          TopBox), nes numatytojo kambario aukščio (250cm) ir baldo aukščio (~245cm)
          skirtumas per mažas — iš šio vaizdo kampo spindulys į Stogelį pirmiau
          atsitrenkia į lubas. Lubos neturi JOKIO onClick/onPointer visame šiame
          faile, tad raycast išjungimas nieko nepakeičia vartotojui matomai —
          vartotojas: "vis dar paspaudus ant stogelio plokstes man neatsiranda
          mygtukas kur galeciau istraukti arba grazinti i karkaso plokstuma".
      */}
      <Box
        position={[floorX, wh + 0.025, maxFloorDepth / 2]}
        args={[floorWidth, 0.05, maxFloorDepth]}
        raycast={() => null}
      >
        <meshStandardMaterial color="#ffffff" side={THREE.DoubleSide} bumpMap={wallBump} bumpScale={WALL_BUMP_SCALE} />
      </Box>

      {/* Galinė siena — praplatinta per šoninių sienų storį, kad jos kraštai susitiktų
          su šoninių sienų išoriniais paviršiais (kai jų yra) */}
      {showBack && (() => {
        const backWallWidth = ww + leftInset + rightInset;
        const backWallX = (ww + rightInset - leftInset) / 2;
        return (
          <group>
            <Box
                position={[backWallX, wh / 2 - 0.025, -wt / 2]}
                args={[backWallWidth, wh + 0.05, wt]}
            >
                <meshStandardMaterial color="#ffffff" bumpMap={wallBump} bumpScale={WALL_BUMP_SCALE} />
            </Box>

            {renderSkirting && (
                <Box
                    position={[backWallX, skirtingHeight / 2, skirtingThickness / 2]}
                    args={[backWallWidth, skirtingHeight, skirtingThickness]}
                >
                    <meshStandardMaterial color="#999999" />
                </Box>
            )}
          </group>
        );
      })()}

      {/* Kairė siena — stovi IŠ KAIRĖS už grindų krašto (x=0), ne grindų ribose */}
      {showLeft && (
          <group>
            <Box
                position={[-wt / 2, wh / 2, wdLeft / 2]}
                args={[wt, wh + 0.1, wdLeft]}
            >
                <meshStandardMaterial color="#ffffff" bumpMap={wallBump} bumpScale={WALL_BUMP_SCALE} />
            </Box>
            {renderSkirting && (
                <Box
                    position={[skirtingThickness / 2, skirtingHeight / 2, wdLeft / 2]}
                    args={[skirtingThickness, skirtingHeight, wdLeft]}
                >
                    <meshStandardMaterial color="#999999" />
                </Box>
            )}
          </group>
      )}

      {/* Dešinė siena — stovi IŠ DEŠINĖS už grindų krašto (x=ww), ne grindų ribose */}
      {showRight && (
          <group>
            <Box
                position={[ww + wt / 2, wh / 2, wdRight / 2]}
                args={[wt, wh + 0.1, wdRight]}
            >
                <meshStandardMaterial color="#ffffff" bumpMap={wallBump} bumpScale={WALL_BUMP_SCALE} />
            </Box>
            {renderSkirting && (
                 <Box
                    position={[ww - skirtingThickness / 2, skirtingHeight / 2, wdRight / 2]}
                    args={[skirtingThickness, skirtingHeight, wdRight]}
                >
                    <meshStandardMaterial color="#999999" />
                </Box>
            )}
          </group>
      )}
    </group>
  );
};

export default Wall;
